import { ArrowRight, CalendarClock } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';

import type { StorefrontCareers, StorefrontJobOpening } from '@/features/storefront/api/careers';
import { formatCareerDate, openingChips } from '@/features/storefront/careers';
import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import type { CareersSection } from '@/features/storefront/sections/schema';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';

/**
 * The shop's open positions as a list of cards: the role, the facts a job
 * seeker scans by (department, place, hours, pay) and a line about it, each
 * opening its own page at `/careers/{slug}`.
 *
 * One list for two places - the `careers` section on any page, and the whole
 * `/careers` page - so a role reads the same wherever a shopper meets it.
 */

/** How a link is drawn: the caller's own, so preview links stay inert. */
export type CareersLink = (
  href: string,
  className: string,
  children: ReactNode,
  style?: CSSProperties,
) => ReactNode;

const MUTED: CSSProperties = { color: 'var(--storefront-muted)' };
const LINE: CSSProperties = { borderColor: 'color-mix(in srgb, currentColor 12%, transparent)' };
const CHIP: CSSProperties = {
  ...LINE,
  backgroundColor: 'color-mix(in srgb, var(--storefront-primary) 6%, transparent)',
};

/** The role's facts as small pills, shared with the role's own page. */
export function OpeningChips({ opening }: { opening: StorefrontJobOpening }) {
  const copy = useShopContent();
  const chips = openingChips(copy, opening);
  if (chips.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={copy('careers.details')} data-opening-chips>
      {chips.map((chip, index) => (
        <li key={`${index}-${chip}`} className="rounded-full border px-2.5 py-0.5 text-xs" style={CHIP}>
          {chip}
        </li>
      ))}
    </ul>
  );
}

/**
 * `list` is one role per row, the link at the row's end - for a section
 * sharing the page with others. `grid` is cards side by side, the link at
 * the card's foot - for the `/careers` page, which has the width to spare.
 */
export type OpeningLayout = 'list' | 'grid';

function OpeningCard({
  opening,
  showSummary,
  link,
  layout,
}: {
  opening: StorefrontJobOpening;
  showSummary: boolean;
  link: CareersLink;
  layout: OpeningLayout;
}) {
  const copy = useShopContent();
  const { locale } = useStorefrontLanguage();
  const closes = formatCareerDate(opening.closes_at, locale);
  const href = `/careers/${encodeURIComponent(opening.slug)}`;
  return (
    <li
      className={
        layout === 'grid'
          ? 'flex flex-col gap-4 border p-5 transition-shadow hover:shadow-md'
          : 'flex flex-col gap-3 border p-5 sm:flex-row sm:items-center sm:justify-between'
      }
      style={{ ...LINE, borderRadius: 'var(--storefront-radius)' }}
      data-opening={opening.slug}
    >
      <div className="min-w-0 space-y-2">
        <h3 className="text-lg" style={STOREFRONT_HEADING_STYLE}>
          {link(href, 'hover:underline', opening.title)}
        </h3>
        <OpeningChips opening={opening} />
        {showSummary && opening.summary ? (
          <p className={layout === 'grid' ? 'line-clamp-3 text-sm' : 'text-sm'} style={MUTED}>
            {opening.summary}
          </p>
        ) : null}
        {closes ? (
          <p className="inline-flex items-center gap-1.5 text-xs" style={MUTED}>
            <CalendarClock className="size-3.5" aria-hidden />
            {copy('careers.closes', { date: closes })}
          </p>
        ) : null}
      </div>
      <div className={layout === 'grid' ? 'mt-auto border-t pt-4' : 'shrink-0'} style={layout === 'grid' ? LINE : undefined}>
        {link(
          href,
          'inline-flex items-center gap-1.5 text-sm font-semibold hover:underline',
          <>
            {copy('careers.view_role')}
            <ArrowRight className="size-4" aria-hidden />
          </>,
          { color: 'var(--storefront-primary)' },
        )}
      </div>
    </li>
  );
}

export function OpeningList({
  openings,
  showSummary = true,
  link,
  layout = 'list',
}: {
  openings: readonly StorefrontJobOpening[];
  showSummary?: boolean;
  link: CareersLink;
  layout?: OpeningLayout;
}) {
  return (
    <ul className={layout === 'grid' ? 'grid gap-5 md:grid-cols-2' : 'space-y-4'} data-careers-list>
      {openings.map((opening) => (
        <OpeningCard key={opening.id} opening={opening} showSummary={showSummary} link={link} layout={layout} />
      ))}
    </ul>
  );
}

export interface CareersViewProps {
  section: CareersSection;
  /** `null` while the list is still on its way. */
  careers: StorefrontCareers | null;
  /** Whether the shop has its Careers page switched on. */
  enabled: boolean;
  /** The customiser: says why the section is empty instead of vanishing. */
  preview: boolean;
  heading: ReactNode;
  /** What the customiser shows while there is nothing to list. */
  emptyHint: ReactNode;
  link: CareersLink;
  /** The "View all" words, wrapped for typing into on the page where allowed. */
  ctaText?: (value: string) => ReactNode;
}

export function CareersView({
  section,
  careers,
  enabled,
  preview,
  heading,
  emptyHint,
  link,
  ctaText = (value) => value,
}: CareersViewProps) {
  const copy = useShopContent();
  const openings = enabled ? (careers?.openings ?? []) : [];

  if (openings.length === 0) {
    // Still loading, switched off or nothing to show: the live band takes its
    // padding with it, and the customiser explains how to fill it.
    if (!preview || (enabled && careers === null)) return <div data-section-empty hidden />;
    return (
      <Container>
        {heading}
        {emptyHint}
      </Container>
    );
  }

  const shown = openings.slice(0, section.limit);
  const label = section.cta_label?.trim() || copy('careers.view_all');

  return (
    <Container>
      {heading}
      <OpeningList openings={shown} showSummary={section.show_summary} link={link} />
      <div className="mt-6">
        {link(
          '/careers',
          'inline-flex items-center gap-1.5 text-sm font-semibold hover:underline',
          <>
            {ctaText(label)}
            <ArrowRight className="size-4" aria-hidden />
          </>,
          { color: 'var(--storefront-primary)' },
        )}
      </div>
    </Container>
  );
}
